import React, {Component} from 'react';
import PropTypes from 'prop-types';

import * as d3 from 'd3';
import _ from 'lodash';

import {formatKmbt} from '../utils/d3-mixins'

class LineChartGrid extends Component {

  shouldComponentUpdate(nextProps) {
    const widthDifferent = nextProps.width !== this.props.width;
    const heightDifferent = nextProps.height !== this.props.height;
    const dataDifferent = nextProps.data !== this.props.data;
    const dimYDiff = nextProps.dimY !== this.props.dimY;
    const dimXDiff = nextProps.dimX !== this.props.dimX;
    const yearDiff = nextProps.year !== this.props.year;

    return widthDifferent || heightDifferent || dataDifferent || dimYDiff || yearDiff || dimXDiff;
  }

  componentDidUpdate() {
    setTimeout(this.renderD3.bind(this), 400);
  }

  renderD3() {
    const width = this.props.width;
    const height = this.props.height;
    const props = this.props;

    const parseYear = d3.timeParse("%Y");
    const years = [2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015];
    const data = this.props.data;

    const dataX = _.chain(years)
      .reduce((reducer, year) => {
        const yearIndex = 2015 - (year || 2015);

        reducer.push({
          sa: data.sa,
          sn: data.sn,
          sf: data.sf,
          cf: data.cf,
          cn: data.cn,
          year: year,
          type: 'x',
          x: parseYear(year),
          y: _.get(data, [props.dimX, yearIndex], 0),
        });

        return reducer;
      }, [])
      .value();


    const dataY = _.chain(years)
      .reduce((reducer, year) => {
        const yearIndex = 2015 - (year || 2015);
        reducer.push({
          sa: data.sa,
          sn: data.sn,
          sf: data.sf,
          cf: data.cf,
          cn: data.cn,
          year: year,
          type: 'y',
          x: parseYear(year),
          y: _.get(data, [props.dimY, yearIndex], 0),
        });

        return reducer;
      }, [])
      .value();

    const valuesYY = _.chain(dataY).map('y').uniq().value();
    const valuesYX = _.chain(dataX).map('y').uniq().value();
    const valuesX = _.chain(years).map(parseYear).uniq().value();

    const x = d3.scaleTime()
      .domain(d3.extent(valuesX))
      .range([0, width]);

    const axisX = d3.axisBottom(x)
      .ticks(d3.timeYear);

    const yy = d3.scaleLinear()
      .domain(d3.extent(valuesYY))
      .range([height, 0]);

    const yx = d3.scaleLinear()
      .domain(d3.extent(valuesYX))
      .range([height, 0]);

    const axisYY = d3.axisRight(yy)
      .tickFormat(formatKmbt)
      .ticks(6);

    const axisYX = d3.axisLeft(yx)
      .tickFormat(formatKmbt)
      .ticks(6);

    const areaX = d3.area()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.x))
      .y1(d => yx(d.y))
      .y0(() => yx(d3.min(valuesYX)));

    const areaY = d3.area()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.x))
      .y1(d => yy(d.y))
      .y0(() => yy(d3.min(valuesYY)));

    const lineX = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.x))
      .y(d => yx(d.y));

    const lineY = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(d.x))
      .y(d => yy(d.y));

    // Add the X Axis
    d3.select(this.refs.xAxis)
      .transition()
      .call(axisX);

    // Add the YY Axis
    d3.select(this.refs.yyAxis)
      .transition()
      .call(axisYY);

    d3.select(this.refs.yxAxis)
      .transition()
      .call(axisYX);

    d3.select(this.refs.areaX)
      .datum(dataX)
      .attr("class", "area-x")
      .transition()
      .attr("d", areaX);

    d3.select(this.refs.areaY)
      .datum(dataY)
      .attr("class", "area-y")
      .transition()
      .attr("d", areaY);

    d3.select(this.refs.lineX)
      .datum(dataX)
      .attr("class", "line-x")
      .transition()
      .attr("d", lineX);

    d3.select(this.refs.lineY)
      .datum(dataY)
      .attr("class", "line-y")
      .transition()
      .attr("d", lineY);

    // Add Dots
    const dotsX = d3.select(this.refs.dotsX)
      .selectAll("circle")
      .data(dataX);

    dotsX.enter().append("circle").each(createDots).each(updateDots);
    dotsX.each(createDots).each(transitionDots);
    dotsX.exit().remove();

    const dotsY = d3.select(this.refs.dotsY)
      .selectAll("circle")
      .data(dataY);

    dotsY.enter().append("circle").each(createDots).each(updateDots);
    dotsY.each(createDots).each(transitionDots);
    dotsY.exit().remove();

    function createDots() {
      d3.select(this)
        .attr("r", 3)
        .attr("class", d => {
          return 'dot ' + (d.type === 'x' ? 'red' : 'blue')
        })
        .on("mouseover", d => {
          d3.select(this).attr("r", 5);

          const coords = [x(d.x), d.type === 'x' ? yx(d.y) : yy(d.y)];
          const content = (
            <div>
              <b className={d.type === 'x' ? 'red' : 'blue'}>
                {d.type === 'x' ? props.titleX : props.titleY} : </b>
              <i>{d3.format(',')(d.y)}</i><br/>
            </div>
          );

          if (props.onHoverOver) {
            props.onHoverOver({coords, props: {}, data: {...d, content}})
          }
        })
        .on("mouseout", d => {
          d3.select(this).attr("r", 3);

          if (props.onHoverOut) {
            props.onHoverOut()
          }
        })
        .on("click", d => {
          if (props.onClick) {
            props.onClick(d.year);
          }
        })
    }

    function updateDots() {
      d3.select(this)
        .attr("cx", d => x(d.x))
        .attr("cy", d => {
          return d.type === 'x' ? yx(d.y) : yy(d.y);
        })
    }

    function transitionDots() {
      d3.select(this)
        .attr("cx", d => x(d.x))
        .transition()
        .attr("cy", d => {
          return d.type === 'x' ? yx(d.y) : yy(d.y);
        })
    }

    if (props.year) {
      d3.select(this.refs.highlight)
        .attr('class', 'highlight-bar')
        .attr('width', 2)
        .attr("rx", 1)
        .attr("ry", 1)
        .attr('height', height)
        .transition()
        .duration(300)
        .attr('transform', "translate(" + (x(parseYear(props.year)) - 1) + ",0)")
    }
  }

  render() {
    // TODO can get better perf if we move some of these layers into new react modules with update logic
    return (
      <g>
        <g className="axis">
          <g ref="xAxis" transform={"translate(0," + this.props.height + ")"}></g>
          <g className="axis-left" ref="yxAxis"></g>
          <g className="axis-right" ref="yyAxis" transform={"translate(" + this.props.width + ",0)"}></g>
        </g>
        <path ref="areaY"></path>
        <path ref="areaX"></path>
        <g>
          <rect ref="highlight"></rect>
        </g>
        <path ref="lineY"></path>
        <path ref="lineX"></path>
        <g ref="dotsY"></g>
        <g ref="dotsX"></g>
      </g>
    );
  }
}

LineChartGrid.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dimX: PropTypes.string,
  dimY: PropTypes.string,
  year: PropTypes.number,
  data: PropTypes.object,
  onHoverOver: PropTypes.func,
  onHoverOut: PropTypes.func
};

export default LineChartGrid;




// WEBPACK FOOTER //
// ./src/charts/AreaChart/AreaChartGrid.js